import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Project } from "../../types/projectTypes";
import { getActiveProjects } from "../../api/projectApi";
import { getUserProject } from "../../api/userApi";
import { profiles, ADMIN_SISTEMA } from "../../contexts/constants/profiles";
import moment from "moment";
import { getFullRasReport } from "../../api/productionControlApi"; // <--- NUEVA API
import RasReportTable from "../../components/RasReportTable";
// IMPORTACIÓN CORREGIDA: Usamos los nuevos tipos
import {
  RasReportRow,
  FullRasReportResponse,
  DataChart
} from "../../types/rasTypes";
import RasReportForm from "../../components/RasReportForm";
import ExcelJS from "exceljs";
import logo from "../../assets/images/logoElecnor.png";
import Loader from "../../components/Loader";
import {
  Chart as Chartjs,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

// Registrar componentes de Chart.js
Chartjs.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

const RasReport: React.FC = () => {
  // Estados
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<number>(0);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [highlightedDates, setHighlightedDates] = useState<Date[]>([]);

  // Estados de datos
  const [reportRows, setReportRows] = useState<RasReportRow[]>([]);
  const [chartData, setChartData] = useState<DataChart>({ labels: [], datasets: [] });

  // Estados de UI
  const [loading, setLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState({ show: false, message: "" });
  const [userRole, setUserRole] = useState<string>("");

  // Filtros Visuales
  const [stage, setStage] = useState<string>("Todas");
  const [activityFilter, setActivityFilter] = useState<string>("Todas");
  const [uniqueSections, setUniqueSections] = useState<string[]>([]);
  const [uniqueActivities, setUniqueActivities] = useState<string[]>([]);
  const [isSelectedStage, setIsSelectedStage] = useState<boolean>(false);

  // Carga inicial de proyectos
  useEffect(() => {
    const loadProjects = async () => {
      setLoading(true);
      try {
        const userStr = localStorage.getItem("user");
        let rut = "", role = "";
        if (userStr) {
            const user = JSON.parse(userStr);
            rut = user.identificationNum;
            role = user.rols?.description;
            setUserRole(role);
        }
        let projectsData: Project[] = [];
        if (role === profiles[0].name || role === profiles[2].name || role === profiles[3].name || role === ADMIN_SISTEMA) {
            projectsData = await getActiveProjects();
        } else {
            const userProjects = await getUserProject(rut);
            projectsData = Array.isArray(userProjects) ? userProjects : [];
        }
        setProjects(projectsData.filter((p) => p.status === undefined || p.status === true));
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    loadProjects();
  }, []);

  // Manejadores de Eventos
  const handleProject = (e: SelectChangeEvent<number>) => {
    setSelectedProject(Number(e.target.value));
    setReportRows([]); // Limpiar al cambiar proyecto
    setStage("Todas");
    setActivityFilter("Todas");
    setIsSelectedStage(false);
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
      // Calcular semana
      const m = moment(date);
      const start = m.clone().startOf("isoWeek");
      const end = m.clone().endOf("isoWeek").subtract(1, "days");
      const dates = [];
      for (let day = start; day.isSameOrBefore(end); day.add(1, "days")) {
        dates.push(day.toDate());
      }
      setHighlightedDates(dates);
    }
  };

  const handleStage = (e: SelectChangeEvent<string>) => {
    const val = e.target.value;
    setStage(val);
    if(val !== "Todas") {
        setIsSelectedStage(true);
        // Filtrar actividades únicas para este stage
        const acts = Array.from(new Set(reportRows.filter(r => r.section === val).map(r => r.name)));
        setUniqueActivities(acts);
    } else {
        setIsSelectedStage(false);
        setActivityFilter("Todas");
    }
    setActivityFilter("Todas");
  };

  const handleActivity = (e: SelectChangeEvent<string>) => {
    setActivityFilter(e.target.value);
  };

  // --- FUNCIÓN PRINCIPAL: FILTRAR (Llamar al Backend) ---
  const onClickFilter = async () => {
    if (!selectedProject || !selectedDate) {
        setShowModal({ show: true, message: "Seleccione proyecto y fecha." });
        return;
    }
    setLoading(true);
    try {
        const isoStart = highlightedDates[0].toISOString();
        const isoEnd = highlightedDates[highlightedDates.length - 1].toISOString();

        // LLAMADA AL NUEVO ENDPOINT
        const response: FullRasReportResponse = await getFullRasReport(selectedProject, isoStart, isoEnd);

        // Guardamos los datos puros que vienen del backend
        setReportRows(response.rows);

        // Extraemos secciones únicas para el filtro
        const sections = Array.from(new Set(response.rows.map(r => r.section)));
        setUniqueSections(sections);

        // Generamos el Gráfico con TODOS los datos iniciales
        updateChart(response.rows);

    } catch (error) {
        console.error(error);
        setShowModal({ show: true, message: "Error al cargar datos." });
    } finally {
        setLoading(false);
    }
  };

  const updateChart = (rows: RasReportRow[]) => {
    const labels = rows.map(r => `${r.name} ${r.subActivity ? `(${r.subActivity})` : ''}`);
    const dataProgress = rows.map(r => Math.round(r.activityProgress || 0));
    const dataProgrammed = rows.map(r => r.totalScope > 0 ? Math.round((r.programmedAdvance / r.totalScope) * 100) : 0);

    setChartData({
        labels,
        datasets: [
            {
                type: 'bar',
                label: 'Total avance a la fecha (%)',
                backgroundColor: 'rgba(75, 192, 192, 0.6)',
                data: dataProgress,
                stack: 'Stack 0'
            },
            {
                type: 'line',
                label: 'Total programado a la fecha (%)',
                borderColor: 'rgba(255, 99, 132, 1)',
                borderWidth: 2,
                fill: false,
                data: dataProgrammed
            }
        ]
    });
  };

  // Efecto para actualizar gráfico cuando cambian los filtros visuales
  useEffect(() => {
      if(reportRows.length > 0) {
          const visibleRows = getVisibleRows();
          updateChart(visibleRows);
      }
  }, [stage, activityFilter, reportRows]);

  // Filtrado Visual de la Tabla (Client-side filtering)
  const getVisibleRows = () => {
    return reportRows.filter(row => {
        if (stage !== "Todas" && row.section !== stage) return false;
        if (activityFilter !== "Todas" && row.name !== activityFilter) return false;
        return true;
    });
  };

  const onClickExportReport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Informe Ras");

    // Logo y Cabeceras (Mismo código que tenías, ajustado a los nuevos datos)
    try {
        const imageResponse = await fetch(logo);
        const imageBuffer = await imageResponse.arrayBuffer();
        const imageId = workbook.addImage({ buffer: imageBuffer, extension: "png" });
        worksheet.addImage(imageId, { tl: { col: 0, row: 1 }, ext: { width: 160, height: 60 } });
    } catch(e) { console.warn("No se pudo cargar logo"); }

    const projectName = projects.find((p) => p.id === selectedProject);
    if (projectName) {
      const cell = worksheet.getCell("D2");
      cell.value = `PROYECTO: ${projectName.name?.toUpperCase() || ""}`;
      cell.font = { bold: true, size: 18 };
    }
    const dateCell = worksheet.getCell("D3");
    dateCell.value = `FECHA: ${new Date().toLocaleDateString("es-ES")}`;

    const startRow = 6;
    worksheet.getRow(startRow).values = [
      "Etapa", "Actividad", "Subcontrato", "Fecha Finalización Inicial", "Unidad", "Cantidad",
      "Acumulado Programa Inicial", "Desvío Programa Inicial",
      "Ejecutado S-3", "Ejecutado S-2", "Ejecutado S-1", "Acumulado anterior",
      "Ejecutadas en el periodo", "Acumulado Actual", "Avance a la fecha",
      "Programado en el periodo", "Desviación en el periodo",
      "Núm. Cuadrillas (Ejecutado)", "Núm. Cuadrillas (Plan)",
      "Plan S+1", "Plan S+2", "Plan S+3", "Acumulado Plan S+3", "Nva. Fecha Finalización",
    ];

    // Estilo neutro de encabezados (sin código de colores)
    const headerRow = worksheet.getRow(startRow);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FF004F86" }, size: 10 };
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
      cell.border = { bottom: { style: "thin" }, right: { style: "thin" } };
    });

    // Redondea a un máximo de 2 decimales (deja el valor como número para Excel)
    const round2 = (value: number | string | undefined | null): number => {
      const num = Number(value);
      if (value === undefined || value === null || isNaN(num)) return 0;
      return Math.round(num * 100) / 100;
    };

    const visibleRows = getVisibleRows();
    visibleRows.forEach((row) => {
      const r = worksheet.addRow([
        row.section,
        `${row.name} ${row.subActivity ? `(${row.subActivity})` : ''}`,
        // C / D — Tier 3 (placeholder, sin fuente de datos aún)
        row.subcontract ?? "",
        row.initialFinishDate ?? "",
        row.unit,
        round2(row.totalScope),
        // G / I — Tier 3 (placeholder)
        row.initialProgramAccum != null ? round2(row.initialProgramAccum) : "",
        row.initialProgramDeviation != null ? round2(row.initialProgramDeviation) : "",
        // J / K / L — Ejecutado semanas previas
        round2(row.executedS3),
        round2(row.executedS2),
        round2(row.executedS1),
        // Acumulado anterior (conservada)
        round2(row.previousExecuted),
        // M / N / O / P / Q
        round2(row.currentExecuted),
        round2(row.totalProgress),
        round2(row.activityProgress),
        round2(row.programmedAdvance),
        round2(row.weeklyDeviation),
        // R / S — Cuadrillas
        round2(row.executedCrews),
        round2(row.plannedCrews),
        // T / U / V / W — Plan futuro
        round2(row.planS1),
        round2(row.planS2),
        round2(row.planS3),
        round2(row.planAccumS3),
        // X — Tier 3 (placeholder)
        row.newFinishDate ?? "",
      ]);
      r.eachCell((cell) => { cell.alignment = { horizontal: "center", vertical: "middle" }; });
    });

    // Descarga
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `informe_ras_${new Date().toISOString().split("T")[0]}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3, height: "100%" }}>
      <Typography variant="h4" marginBottom={5}>Resumen Semanal</Typography>

      {showModal.show && (
        <Dialog open={showModal.show} onClose={() => setShowModal({ ...showModal, show: false })}>
          <DialogTitle>{showModal.message}</DialogTitle>
          <DialogActions><Button onClick={() => setShowModal({ ...showModal, show: false })}>Aceptar</Button></DialogActions>
        </Dialog>
      )}

      {loading && <Loader />}

      <Box sx={{ p: 3 }}>
        <RasReportForm
          projects={projects}
          selectedProject={selectedProject}
          selectedDate={selectedDate}
          stage={stage}
          activity={activityFilter}
          highlightedDates={highlightedDates}
          uniqueSections={uniqueSections}
          uniqueActivities={uniqueActivities}
          isSelectedStage={isSelectedStage}
          handleProject={handleProject}
          handleDateChange={handleDateChange}
          handleStage={handleStage}
          handleActivity={handleActivity}
          onClickFilter={onClickFilter}
        />

        <Grid style={{ marginTop: 50 }}>
          {/* IMPORTANTE: Usamos las filas filtradas y el nuevo componente Table */}
          <RasReportTable dataRas={getVisibleRows()} />
        </Grid>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="contained" disabled={reportRows.length === 0} onClick={onClickExportReport}>
            Exportar informe
          </Button>
        </Box>

        {reportRows.length > 0 && (
          <Box sx={{ width: "100%", mt: 10, display: "flex", justifyContent: "center" }}>
            <Bar data={chartData as any} options={{ responsive: true, plugins: { legend: { position: "top" } } }} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RasReport;